/**
 * 资源树 / 开天菜单树：根节点为接口 resourceLevel===1 的实体节点（不再补虚拟根），
 * 同级按 sortNum 排序，子节点按 parentCode 与 resourceCode 关联。
 */

function sortKey(node: any): number {
  const value = node?.sortNum ?? node?.sort ?? node?.sortOrder
  const num = Number(value)
  return Number.isFinite(num) ? num : 0
}

function compareSiblings(a: any, b: any): number {
  const diff = sortKey(a) - sortKey(b)
  if (diff !== 0) return diff
  return String(a.resourceName || a.label || '').localeCompare(
    String(b.resourceName || b.label || ''),
    'zh-CN'
  )
}

function finalizeTree(nodes: any[], level: number): void {
  for (const node of nodes) {
    // node.__treeLevel = level
    const children = node.children as any[]
    if (children?.length) {
      children.sort(compareSiblings)
      finalizeTree(children, level + 1)
    }
  }
}

/** 列表 resourceLevel：明确为 1 的根；缺失时回退为「未被任何节点挂为子」的旧根判定 */
function isResourceTreeRoot(node: any, childCodes: Set<string>): boolean {
  const code = node.resourceCode != null ? String(node.resourceCode) : ''
  const rl = Number(node.resourceLevel)
  if (Number.isFinite(rl)) {
    return rl === 1 && !childCodes.has(code)
  }
  return !childCodes.has(code)
}

/**
 * 将平铺资源按 parentCode -> resourceCode 组装成树。
 * - 根：resourceLevel===1 且未被挂到任一父下；无 resourceLevel 时与旧逻辑一致（无父链的顶层）
 * - 不创建虚拟父节点；同级按 sortNum 排序
 */
export function buildResourceTree(list: any[]): any[] {
  if (!Array.isArray(list) || !list.length) return []

  const byCode = new Map<string, any>()

  list.forEach((raw: any) => {
    const code = raw?.resourceCode != null ? String(raw.resourceCode) : ''
    if (!code) return
    byCode.set(code, { ...raw, children: [] as any[] })
  })

  const childCodes = new Set<string>()
  for (const raw of list) {
    const code = raw?.resourceCode != null ? String(raw.resourceCode) : ''
    if (!code) continue

    const node = byCode.get(code)
    if (!node) continue

    const parentCode = raw?.parentCode != null ? String(raw.parentCode) : ''
    if (!parentCode) continue

    const parent = byCode.get(parentCode)
    if (!parent || parent === node) continue
    if (String(parent.resourceCode) === String(node.resourceCode)) continue

    parent.children.push(node)
    childCodes.add(code)
  }

  const roots: any[] = []
  for (const node of byCode.values()) {
    if (isResourceTreeRoot(node, childCodes)) roots.push(node)
  }

  roots.sort((a, b) => compareSiblings(a, b))
  finalizeTree(roots, 1)
  return roots
}

/** 深度优先展开资源树节点（需先经 buildResourceTree 赋 __treeLevel） */
export function flattenResourceTree(nodes: any[]): any[] {
  const out: any[] = []
  const walk = (arr: any[]) => {
    for (const n of arr || []) {
      out.push(n)
      if (Array.isArray(n.children) && n.children.length) walk(n.children)
    }
  }
  walk(nodes)
  return out
}

// ---------- 开天 pcMenuTree（原 auth.ts resourcesToPcMenuTree） ----------

function menuSortKey(n: any): number {
  const num = parseInt(String(n.sort), 10)
  return Number.isFinite(num) ? num : 0
}

function compareMenuSiblings(a: any, b: any): number {
  const d = menuSortKey(a) - menuSortKey(b)
  if (d !== 0) return d
  return String(a.name).localeCompare(String(b.name), 'zh-CN')
}

/** 子层按 sort（来自 sortNum）升序，同名再比 name */
function finalizeMenuChildren(nodes: any[]): void {
  for (const n of nodes) {
    const ch = n.children as any[]
    if (ch?.length) {
      ch.sort(compareMenuSiblings)
      finalizeMenuChildren(ch)
    }
  }
}

function isPcMenuRootNode(n: any, childCodes: Set<string>): boolean {
  const code = String(n.code ?? '')
  const rl = Number(n.resourceLevel)
  if (Number.isFinite(rl)) {
    return rl === 1 && !childCodes.has(code)
  }
  return !childCodes.has(code)
}

/**
 * 将资源管理平铺结构转为开天 pcMenuTree（code / parentCode / name / url / children 等）
 * - 根：resourceLevel===1 的实体；无 resourceLevel 时回退为未被挂为子的顶层节点
 * - 不补虚拟根；根层按 sortNum（sort 字段）排序，子层递归同样规则
 */
export function resourcesToPcMenuTree(list: any[]): any[] {
  console.info('🚀 ~ file:resourceTree method:resourcesToPcMenuTree line:139 ----- list', list.length)
  if (!Array.isArray(list) || !list.length) return []

  const byCode = new Map<string, any>()

  const toLeafNode = (raw: any) => {
    const rl = raw?.resourceLevel
    const resourceLevel = rl != null && rl !== '' ? Number(rl) : NaN
    return {
      code: String(raw.resourceCode ?? ''),
      parentCode: raw.parentCode != null ? String(raw.parentCode) : '',
      icon: raw.icon ?? '',
      name: String(raw.resourceName ?? raw.name ?? ''),
      id: raw.id,
      sort: String(raw.sortNum ?? 0),
      type: raw.resourceType || 'MENU',
      value: raw.resourceCode,
      url: raw.resourcePath,
      resourceLevel: Number.isFinite(resourceLevel) ? resourceLevel : undefined,
      children: [] as any[]
    }
  }

  list.forEach((raw: any) => {
    const code = raw?.resourceCode != null ? String(raw.resourceCode) : ''
    if (!code) return
    byCode.set(code, toLeafNode(raw))
  })

  const childCodes = new Set<string>()
  for (const raw of list) {
    const code = raw?.resourceCode != null ? String(raw.resourceCode) : ''
    if (!code) continue
    const node = byCode.get(code)
    if (!node) continue
    const pc = raw?.parentCode != null ? String(raw.parentCode) : ''
    if (!pc) continue
    const parent = byCode.get(pc)
    if (parent && parent !== node && String(parent.code) !== String(node.code)) {
      parent.children.push(node)
      childCodes.add(code)
    }
  }

  const roots: any[] = []
  for (const node of byCode.values()) {
    if (isPcMenuRootNode(node, childCodes)) roots.push(node)
  }

  roots.sort(compareMenuSiblings)
  finalizeMenuChildren(roots)

  return roots
}
